import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchPublicPropertyById, looksLikeUuid } from './publicPropertyApi';
import { supabase } from './supabaseClient';
import PublicHeader from './PublicHeader';
import './Enquiry.css';

function Enquiry() {
  const { id } = useParams();
  const [property, setProperty] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');

  React.useEffect(() => {
    setSubmitted(false);
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      const { data, error } = await fetchPublicPropertyById(id);
      if (cancelled) return;
      if (error) setLoadError(error.message || String(error));
      setProperty(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError('');

    if (!supabase) {
      setSaveError(
        'Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env.local.'
      );
      return;
    }

    setSaving(true);
    try {
      const propertyId = looksLikeUuid(property.id) ? property.id : null;
      const { error } = await supabase.from('property_enquiries').insert({
        property_id: propertyId,
        listing_title: (property.title || '').trim(),
        full_name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
      });
      if (error) throw error;
      setSubmitted(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setSaveError(
        err?.message ||
          err?.error_description ||
          'Could not save your enquiry. Run sql/add_property_enquiries_supabase.sql in Supabase.'
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="eq-root">
        <PublicHeader />
        <div className="eq-missing">
          <p className="eq-missing-text">Loading…</p>
          <Link to="/" className="eq-link">
            ← Back to listings
          </Link>
        </div>
      </div>
    );
  }

  if (loadError || !property) {
    return (
      <div className="eq-root">
        <PublicHeader />
        <div className="eq-missing">
          <p className="eq-missing-text">
            {loadError || 'We couldn’t find that property.'}
          </p>
          <Link to="/" className="eq-link">
            ← Back to listings
          </Link>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="eq-root">
        <PublicHeader />
        <div className="eq-success">
          <div className="eq-success-card">
            <div className="eq-success-icon" aria-hidden="true">
              ✓
            </div>
            <h1 className="eq-success-title">Thanks, {name.trim() || 'there'}!</h1>
            <p className="eq-success-text">
              Your enquiry about <strong>{property.title}</strong> has been
              received. We’ll get back to you within one business day.
            </p>
            <div className="eq-success-actions">
              <Link
                to={`/property/${property.id}`}
                className="eq-btn eq-btn-soft"
              >
                Back to property
              </Link>
              <Link to="/" className="eq-btn eq-btn-primary">
                Browse more homes
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="eq-root">
      <PublicHeader />

      <main className="eq-main">
        <div className="eq-summary">
          <div className="eq-summary-media">
            <img src={property.image} alt="" />
          </div>
          <h1 className="eq-heading">Enquire</h1>
          <p className="eq-property-name">{property.title}</p>
          <p className="eq-property-loc">{property.location}</p>
          <p className="eq-property-rent">
            £{property.rent.toLocaleString()}
            <span> / month</span>
          </p>
          <Link to={`/property/${property.id}`} className="eq-summary-link">
            View full listing →
          </Link>
        </div>

        <div className="eq-panel">
          <h2 className="eq-panel-title">Send a message</h2>
          <p className="eq-panel-lead">
            Tell us a little about you and when you’d like to move. We’ll match
            you with the right person on our team.
          </p>

          <form className="eq-form" onSubmit={handleSubmit}>
            {saveError ? (
              <p className="eq-save-error" role="alert" style={{ marginBottom: 12 }}>
                {saveError}
              </p>
            ) : null}
            <div className="eq-field">
              <label className="eq-label" htmlFor="eq-name">
                Full name
              </label>
              <input
                id="eq-name"
                className="eq-input"
                type="text"
                name="name"
                autoComplete="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
              />
            </div>
            <div className="eq-field">
              <label className="eq-label" htmlFor="eq-email">
                Email
              </label>
              <input
                id="eq-email"
                className="eq-input"
                type="email"
                name="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </div>
            <div className="eq-field">
              <label className="eq-label" htmlFor="eq-phone">
                Phone <span className="eq-optional">(optional)</span>
              </label>
              <input
                id="eq-phone"
                className="eq-input"
                type="tel"
                name="phone"
                autoComplete="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+44 7xxx xxxxxx"
              />
            </div>
            <div className="eq-field">
              <label className="eq-label" htmlFor="eq-message">
                Message
              </label>
              <textarea
                id="eq-message"
                className="eq-textarea"
                name="message"
                rows={5}
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Preferred move-in date, household size, any questions…"
              />
            </div>
            <button
              type="submit"
              className="eq-btn eq-btn-primary eq-btn-block"
              disabled={saving}
            >
              {saving ? 'Sending…' : 'Submit enquiry'}
            </button>
            <p className="eq-footnote">
              Your message is stored in Supabase. Add email or CRM notifications from your backend
              when you are ready.
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

export default Enquiry;
